import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Media,
  Row,
} from "reactstrap";
import { checkUserToken } from "../../helpers/api/auth";
import {
  closeComplaints,
  getDetailsComplaints,
} from "../../helpers/api/complaints";
import withRedirect from "../../helpers/hoc/withRedirect";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import Avatar from "../common/Avatar";
import ButtonIcon from "../common/ButtonIcon";
import ButtonIconTooltip from "../common/ButtonIconTooltip";
import Flex from "../common/Flex";
import QuillEditor from "../common/QuillEditor";
import { PreloadState } from "../UI/preloading/PreloadState";
import ComposeAttachment from "./ComposeAttachment";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import createMarkup from "../../helpers/createMarkup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReply } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["clean"],
  ],
};

const ComplainDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { role, token } = useSelector((state) => state.auth.user);
  const [content, setContent] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [isReplying, setIsReplying] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isMe, setIsMe] = useState(false);
  const [profile, setProfile] = useState({
    data: false,
    isLoading: true,
    error: false,
  });
  const { data, isLoading } = useSWRFetch(id, getDetailsComplaints);

  const closeTicket = async (id) => {
    try {
      const res = await closeComplaints({ id: id, answer: content });
      if (res.code === SUCCESS_RESPONSE.OK) {
        history.goBack();
        Toast.fire({
          type: "success",
          title: `Se ha cerrado el caso satisfactoriamente.`,
        });
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  useEffect(() => {
    return () => {
      setAttachments([]);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    isMounted && setIsDisabled(!content);
    return () => {
      isMounted = false;
    };
  }, [content]);

  useEffect(() => {
    let isMounted = true;
    if (data) {
      if (data?.data?.files?.length > 0) {
        for (let i = 0; i < data.data.files.length; i++) {
          let newFile = {
            id: i,
            name: data.data.files[i].id + data.data.files[i].file,
            size: null,
            type: data.data.files[i].file,
          };
          isMounted && setAttachments((prev) => [...prev, newFile]);
        }
      }
      if (data?.data?.complaint?.author === "me") {
        isMounted && setIsMe(true);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [data]);

  useEffect(() => {
    let isMounted = true;
    if (isMe) {
      const getProfile = async () => {
        try {
          const { data } = await checkUserToken(token);
          isMounted &&
            setProfile((prev) => {
              return { ...prev, data: data, isLoading: false };
            });
        } catch (e) {
          isMounted &&
            setProfile((prev) => {
              return { ...prev, error: e, isLoading: false };
            });
        }
      };
      getProfile();
    } else {
      isMounted &&
        setProfile((prev) => {
          return { ...prev, isLoading: false };
        });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [isMe]);

  return (
    <>
      <Card className="mb-3">
        <CardBody tag={Flex} justify="between">
          <div>
            <ButtonIconTooltip
              tag={Link}
              to="/dashboard/complaints"
              id="back"
              icon={`arrow-left`}
            >
              Volver al centro de sugerencias
            </ButtonIconTooltip>
          </div>
        </CardBody>
      </Card>
      <Card className="mb-3">
        <PreloadState
          cond={data?.data ? true : false}
          loading={isLoading || profile.isLoading}
        >
          <div value={true}>
            <CardHeader>
              <Row>
                <Col>
                  <Media>
                    {data?.data?.complaint?.author === "me" ? (
                      profile?.data?.image === null ? (
                        <Avatar size="2xl" />
                      ) : (
                        <Avatar
                          src={
                            profile?.data?.image
                              ? profile?.data?.image
                              : undefined
                          }
                          size="2xl"
                        />
                      )
                    ) : data?.data?.complaint?.author?.image ? (
                      <Avatar
                        src={
                        
                          data?.data.complaint.author.image
                        }
                        size="2xl"
                      />
                    ) : (
                      <Avatar /* src={sender.avatarSrc} */ size="2xl" />
                    )}
                    <Media body className="ml-2">
                      <h5 className="mb-0">{data?.data?.complaint?.title}</h5>
                      <span className="text-800 fs--1">
                        <span className="font-weight-semi-bold">
                          {profile.data && isMe
                            ? `Tú`
                            : `${data?.data?.complaint?.author?.firstname} ${data?.data?.complaint?.author?.lastname}`}
                        </span>
                        {!!isMe ? (
                          <span className="ml-1 text-500 px-1">
                            {profile.username}
                          </span>
                        ) : (
                          <span className="ml-1 text-500 px-1">
                            {data?.data?.complaint?.author?.user}
                          </span>
                        )}
                        <span className="ml-1 text-500">
                          {data?.data.complaint?.closed ? (
                            <Badge pill color="soft-dark">
                              Cerrado
                            </Badge>
                          ) : (
                            <Badge pill color="soft-primary">
                              Abierto
                            </Badge>
                          )}
                        </span>
                      </span>
                    </Media>
                  </Media>
                </Col>
                <Col tag={Flex} align="center" xs="auto" className="ml-auto">
                  <small>
                    {moment(data?.data?.complaint?.created_at).from(new Date())}
                  </small>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="bg-light">
              <p
                className={"mb-0"}
                dangerouslySetInnerHTML={createMarkup(data?.data?.description)}
              />
              {attachments.length > 0 && (
                <div className="bg-light px-card py-3">
                  <Flex column inline>
                    {attachments.map((attachment, i) => (
                      <ComposeAttachment
                        {...attachment}
                        key={attachment.id}
                        index={i}
                        downloadPath={`${attachment.type}`}
                        download={true}
                      />
                    ))}
                  </Flex>
                </div>
              )}
            </CardBody>
            {!data?.data?.complaint?.closed && role === "manager" && (
              <CardFooter>
                <Row className="justify-content-between">
                  <Col>
                    <ButtonIcon
                      onClick={() => setIsReplying(true)}
                      icon="reply"
                      size="sm"
                      className="mr-2"
                      color="falcon-default"
                    >
                      Responder
                    </ButtonIcon>
                  </Col>
                </Row>
              </CardFooter>
            )}
          </div>
          <div value={false}>Cargando.</div>
        </PreloadState>
      </Card>

      {!!data?.data.answer && (
        <Card className="mb-3">
          <PreloadState loading={isLoading}>
            <CardBody style={{ background: "#edd7f5" }}>
              <h6>
                <FontAwesomeIcon icon={faReply} className="mr-1" />
                Respuesta:
              </h6>
              <p
                className={"mb-0"}
                dangerouslySetInnerHTML={createMarkup(data?.data.answer)}
              />
            </CardBody>
          </PreloadState>
        </Card>
      )}
      {isReplying && (
        <Card>
          <CardBody className="p-0">
            <QuillEditor
              value={content}
              onChange={setContent}
              modules={modules}
              style={{ height: 150, marginBottom: 44 }}
            />
          </CardBody>
          <CardFooter
            tag={Flex}
            justify="between"
            className="border-top border-200"
          >
            <Flex align="center">
              <Button
                onClick={() => closeTicket(data?.data?.complaint.id)}
                color="primary"
                size="sm"
                className="px-5 mr-2"
                disabled={isDisabled}
                type="submit"
              >
                Enviar
              </Button>
            </Flex>
          </CardFooter>
        </Card>
      )}
    </>
  );
};

export default withRedirect(ComplainDetails);
